import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../../gateway/Authentication/AppContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import AuthService from '../../../gateway/Authentication/AuthService'
import { ConfirmDialogProps } from '../../../gateway/components/ConfirmDialog'
import DataTable from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import { AssignDialogProps } from '../../../gateway/components/AssignDialog'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import Loading from '../../../gateway/layout/Loading'
import TestdataInDbService from './TestdataInDbService'
import GatewayType from '../../../gateway/pages/GatewayType'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import { faPlay, faTrashAlt, faProjectDiagram } from '@fortawesome/free-solid-svg-icons'

const InDbJob: GatewayType = () => {
  const { t } = useTranslation()
  const [inDbConfigs, setInDbJobs] = useState([] as any[])
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter, assignDialogPropsSetter } = useContext(DialogContext)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      await TestdataInDbService.getAllJobs(setInDbJobs)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('testdata.indb.job.name'),
        dataField: 'jobName',
        sort: true,
        classes: 'position-relative',
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.adapter.edit),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-link text-decoration-none'
                tooltip={t('testdata.indb.job.startJob')}
                onClick={() => startJobDialog(t, confirmDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faPlay} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-link text-decoration-none'
                tooltip={t('testdata.indb.job.assignPolicy')}
                onClick={() => assignPoliciesDialog(t, assignDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faProjectDiagram} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deleteJobDialog(t, confirmDialogPropsSetter, setInDbJobs, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, userPermissions, confirmDialogPropsSetter, assignDialogPropsSetter])

  const defaultSorted = [
    {
      dataField: 'jobName',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={inDbConfigs}
        columns={columns}
        caption={t('In-Database Jobs')}
        helpLink={t('testdata.soap.helpLink')}
        captionButtons={[
          {
            onClick: () => createJobDialog(t, editDialogPropsSetter, setInDbJobs),
            text: t('new'),
            permissions: Authorization.adapter.edit,
          },
        ]}
        searchPlaceholder='Search In-Database Jobs'
        defaultSorted={defaultSorted}
      />
    </Loading>
  )
}

function createJobDialog(t: TFunction, editDialogPropsSetter: any, setInDbJobs: React.Dispatch<any>) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.indb.job.createHeader'),
    onSubmit: TestdataInDbService.createJob,
    onSubmitExtraArgs: [t, setInDbJobs],
    data: [
      {
        id: 'jobName',
        type: 'text',
        label: t('testdata.indb.job.name'),
        required: true,
      },
    ],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteJobDialog(t: TFunction, confirmDialogPropsSetter: any, setInDbJobs: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.indb.job.deleteHeader'),
    body: t('testdata.indb.job.deleteBody', { name: row.jobName }),
    submitText: t('yes'),
    onSubmit: TestdataInDbService.deleteJob,
    onSubmitExtraArgs: [row, t, setInDbJobs],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function assignPoliciesDialog(t: TFunction, assignDialogPropsSetter: any, row: any) {
  TestdataInDbService.getPoliciesWithAssigned(row.id).then((policies) => {
    const assignDialogProps: AssignDialogProps = {
      show: true,
      header: t('testdata.indb.job.assignPolicyHeader', { jobName: row.jobName }),
      onSubmit: TestdataInDbService.assingPoliciesToJob,
      onSubmitExtraArgs: [row.id, row.jobName, t],
      data: policies,
      columns: [
        {
          text: t('testdata.indb.policy.name'),
          dataField: 'configName',
          sort: true,
        },
        {
          dataField: 'isAssigned',
          text: t('assign'),
          isAssignField: true,
        },
      ],
    }
    assignDialogPropsSetter(assignDialogProps)
  })
}

function startJobDialog(t: TFunction, confirmDialogPropsSetter: any, row: any) {
  const startDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.indb.job.startJob'),
    body: t('testdata.indb.job.startBody', { name: row.jobName }),
    submitText: t('yes'),
    onSubmit: TestdataInDbService.startJob,
    onSubmitExtraArgs: [row, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(startDialogProps)
}

export default InDbJob
